import { useEffect, useRef, useState } from "react";
import {
  ContentIdentifier,
  ContentType,
  SharedPost,
} from "@dimensional-engineering/dimensional-models";

import styles from "./ShareCaptionModal.module.css";

import {
  PostIconDark,
  PostIconWhite,
} from "@/components/results/Snippets/SnippetInterractionBar/SnippetInterractionBar";
import { useAlertContext } from "@/context/AlertContext";
import Alert from "@/components/shared/Alerts/Alert";
import { createSharedPost } from "../utils";
import { Mixpanel } from "@/helpers/mixpanel";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useAuth } from "@/context/AuthContext";

type Props = {
  onClose: (caption: string) => void;
  contentIdentifier: ContentIdentifier;
  savedCaption: string;
};

export default function ShareCaptionModal(props: Props) {
  const { user } = useAuth();

  const numberOfConnections = useSelector(
    (state: RootState) => state.connections
  ).publicProfiles?.length;
  const { setAlertComponent } = useAlertContext();

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [caption, setCaption] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [rows, setRows] = useState(1);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCaption(e.target.value);
    const textareaLineHeight = 24;
    const previousRows = e.target.rows;
    e.target.rows = 1;

    const currentRows = Math.floor(e.target.scrollHeight / textareaLineHeight);

    if (currentRows === previousRows) {
      e.target.rows = currentRows;
    }

    setRows(currentRows);
  };

  useEffect(() => {
    setCaption(props.savedCaption);
  }, [props.savedCaption]);

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.style.transform = "translateY(0)";
    }
  }, []);

  function onClose(s: string) {
    if (wrapperRef.current) {
      wrapperRef.current.style.transform = "translateY(300%)";
    }
    setTimeout(() => {
      props.onClose(s);
    }, 200);
  }

  function handlePost() {
    if (caption.length > 2048) {
      return setAlertComponent(
        <Alert
          elementName="Alert"
          type="fail"
          message="Your caption cannot exceed 2048 characters."
        />,
        3000
      );
    }
    setLoading(true);
    createSharedPost(props.contentIdentifier, caption)
      .then((res) => {
        const sharedPost = res.data as SharedPost;
        Mixpanel?.track(
          "Post created",
          {
            caption_text: caption,
            content_type:
              props.contentIdentifier.contentType ===
              ContentType.deliveredDailyStory
                ? "daily story"
                : props.contentIdentifier.contentType ===
                  ContentType.periodicQuiz
                ? "daily quiz"
                : props.contentIdentifier.contentType ===
                  ContentType.weeklyTwoByTwo
                ? "weekly chart"
                : props.contentIdentifier.contentType ===
                  ContentType.weeklyTierList
                ? "weekly tier list"
                : props.contentIdentifier.contentType === ContentType.weeklyVenn
                ? "weekly venn"
                : "daily insight",
            content_type_slug:
              sharedPost.postContent.contentIdentifier.contentType ===
              ContentType.periodicQuiz
                ? sharedPost.postContent.periodicQuizData?.periodicQuiz.slug
                : sharedPost.postContent.contentIdentifier.contentType ===
                  ContentType.weeklyTwoByTwo
                ? sharedPost.postContent.weeklyTwoByTwoData?.weeklyTwoByTwo.slug
                : sharedPost.postContent.contentIdentifier.contentType ===
                  ContentType.weeklyTierList
                ? sharedPost.postContent.weeklyTierListData?.weeklyTierList.slug
                : sharedPost.postContent.contentIdentifier.contentType ===
                  ContentType.weeklyVenn
                ? sharedPost.postContent.weeklyVennData?.weeklyVenn.slug
                : sharedPost.postContent.contentIdentifier.contentType ===
                  ContentType.deliveredDailyStory
                ? sharedPost.postContent.dailyStoryData?.deliveredDailyStory
                    .computeableStoryModelSlug
                : sharedPost.postContent.dailyInsightData?.insightDelivery
                    .computeableInsightModelSlug,
            audience_group_size: numberOfConnections,
          },
          user?.uid
        );
        setAlertComponent(
          <Alert elementName="Alert" type="success" message="Post shared" />,
          3000
        );
        return onClose("");
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }

  return (
    <main
      onClick={() => {
        if (!loading) {
          onClose(caption);
        }
      }}
      className={styles.main}
    >
      <div className={styles.backgroundDiv}></div>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className={styles.postDiv}
        ref={wrapperRef}
      >
        <div className={styles.top}>
          <PostIconWhite />
          <p className={styles.postToConnectionsDiv}>Post to connections</p>
        </div>
        <textarea
          autoFocus
          className={loading ? styles.inputLoading : styles.input}
          placeholder="Add a caption..."
          value={caption}
          onChange={handleInput}
          disabled={loading}
          rows={rows}
        />
        <div className={styles.buttonDiv}>
          <button
            className={styles.postButton}
            data-loading={loading ? "true" : "false"}
            onClick={() => handlePost()}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className={styles.inlineSpinner} />
                <span className={styles.postingText}>Posting</span>
              </>
            ) : (
              <>
                <PostIconDark />
                <span className={styles.postText}>Post</span>
              </>
            )}
          </button>
          <p className={styles.buttonDivText}>
            Shared content is available to connections for one week
          </p>
        </div>
      </div>
    </main>
  );
}
